import React, { useState, useEffect, useCallback, memo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Navbar, Nav, NavDropdown, Container } from "react-bootstrap";
import { motion } from "framer-motion";
import SearchModal from "../components/SearchModal";
import { 
  FaSearch, FaHome, FaUniversity, FaGraduationCap, 
  FaClipboardList, FaAward, FaGlobe, FaBook, 
  FaSchool, FaChalkboardTeacher, FaCalendarAlt, FaBookOpen, FaFileContract
} from "react-icons/fa";
import { MdOutlineDateRange, MdHelpOutline } from "react-icons/md";
import { BsFillCreditCard2FrontFill, BsFillCalculatorFill } from "react-icons/bs";
import logo from "../assets/logo.png";

const Header = () => {
  const [expanded, setExpanded] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScroll = window.scrollY;
          // Hysteresis: prevents "dancing" by using different thresholds for scrolling down vs up
          if (currentScroll > 120) {
            setScrolled(true);
          } else if (currentScroll < 40) {
            setScrolled(false);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleSearch = useCallback(() => setShowSearch(prev => !prev), []);

  // Close mobile menu on route change
  useEffect(() => {
    setExpanded(false);
  }, [location]);

  return (
    <header className={`main-header-wrapper ${scrolled ? "header-scrolled" : ""}`}>
      {/* --- TOP ANNOUNCEMENT BAR --- */}
      <div className="top-ticker-bar">
        <Container>
          <div className="ticker-wrapper">
            <span className="ticker-label">LATEST</span>
            <marquee className="ticker-text" scrollamount="4">
              • Admissions Open for Fall 2026 - Apply Now! • Merit-Based Scholarships available for all Undergraduate Programs • New Research Grant announced for Faculty members •
            </marquee>
          </div>
        </Container>
      </div>

      {/* --- BRANDING & SEARCH AREA --- */}
      <div className="branding-row">
        <Container className="d-flex justify-content-between align-items-center h-100">
          <Link to="/" className="navbar-brand-custom">
            <img src={logo} alt="Sukkur IBA University" className="site-logo" />
            <div className="brand-text d-none d-sm-block">
              <span className="brand-main">SUKKUR IBA UNIVERSITY</span>
              <span className="brand-sub">Excellence Through Merit</span>
            </div>
          </Link>

          <div className="header-actions d-flex align-items-center gap-2 gap-md-3">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="search-pill-trigger"
              onClick={toggleSearch}
            >
              <FaSearch className="text-cyan me-0 me-md-2" />
              <span className="d-none d-md-inline">Search programs...</span>
            </motion.div>

            {/* Mobile Hamburger Toggle */}
            <button 
              className={`mobile-toggle-btn d-lg-none ${expanded ? 'active' : ''}`}
              onClick={() => setExpanded(!expanded)}
              aria-label="Toggle navigation"
            >
              <div className="hamburger-box">
                <div className="hamburger-inner"></div>
              </div>
            </button>
          </div>
        </Container>
      </div>

      {/* --- MAIN NAVIGATION --- */}
      <Navbar 
        expand="lg" 
        variant="dark" 
        className="main-navigation-bar"
        expanded={expanded}
      >
        <Container>
          <Navbar.Collapse id="main-nav">
            <Nav className="mx-auto align-items-center w-100 justify-content-center">
              <Nav.Link as={Link} to="/" className={location.pathname === "/" ? "nav-active" : ""}>
                <FaHome className="me-2 ico-nav" /> Home
              </Nav.Link>

              <NavDropdown title={<><FaUniversity className="me-2 ico-nav" /> Admissions</>} id="adm-drop">
                <NavDropdown.Item as={Link} to="/admission-schedule"><MdOutlineDateRange className="drop-ico" /> Schedule</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/fee-structure"><BsFillCreditCard2FrontFill className="drop-ico" /> Fee Structure</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/fees-calculator"><BsFillCalculatorFill className="drop-ico" /> Fee Calculator</NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item as={Link} to="/admission-procedure"><MdHelpOutline className="drop-ico" /> How to Apply</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/admission-policy"><FaFileContract className="drop-ico" /> Admission Policy</NavDropdown.Item>
              </NavDropdown>

              <NavDropdown title={<><FaGraduationCap className="me-2 ico-nav" /> Academics</>} id="acad-drop">
                <NavDropdown.Item as={Link} to="/programs"><FaBook className="drop-ico" /> Degree Programs</NavDropdown.Item>
                <NavDropdown.Item href="https://iba-suk.edu.pk" target="_blank"><FaSchool className="drop-ico" /> Academic Schools</NavDropdown.Item>
                <NavDropdown.Item href="https://edc.iba-suk.edu.pk" target="_blank"><FaChalkboardTeacher className="drop-ico" /> Executive Development</NavDropdown.Item>
              </NavDropdown>

              <NavDropdown title={<><FaClipboardList className="me-2 ico-nav" /> Portals</>} id="port-drop">
                <NavDropdown.Item as={Link} to="/programs"><FaBookOpen className="drop-ico" /> Undergraduate</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/programs"><FaBookOpen className="drop-ico" /> Graduate</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/important-dates"><FaCalendarAlt className="drop-ico" /> Important Dates</NavDropdown.Item>
              </NavDropdown>

              <Nav.Link as={Link} to="/scholarships" className={location.pathname === "/scholarships" ? "nav-active" : ""}>
                <FaAward className="me-2 ico-nav" /> Scholarships
              </Nav.Link>

              <Nav.Link as={Link} to="/campus-tour" className="tour-btn ms-lg-3 mt-3 mt-lg-0">
                <FaGlobe className="me-2 ico-nav" /> Virtual Tour
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <SearchModal showSearch={showSearch} handleCloseSearch={() => setShowSearch(false)} />

      <style>{`
        .main-header-wrapper {
          position: sticky;
          top: 0;
          z-index: 1050;
          background: #002b5a;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Glassmorphism on Scroll */
        .header-scrolled {
          background: rgba(0, 43, 90, 0.85) !important;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.15);
        }

        /* Ticker Styling */
        .top-ticker-bar {
          background: #001a38;
          padding: 6px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          max-height: 40px;
        }
        
        .ticker-wrapper { display: flex; align-items: center; gap: 20px; }
        .ticker-label { 
          background: #00ced1; 
          color: #000; 
          font-size: 0.65rem; 
          font-weight: 800; 
          padding: 2px 10px; 
          border-radius: 4px;
          box-shadow: 0 0 15px rgba(0, 206, 209, 0.3);
        }
        .ticker-text { color: #abdbe3; font-size: 0.8rem; font-weight: 400; }

        /* Branding Styling */
        .branding-row { 
          padding: 12px 0; 
          background: transparent; 
          border-bottom: 1px solid rgba(255,255,255,0.05);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          max-height: 120px;
          opacity: 1;
          overflow: hidden;
        }
        .site-logo { height: 60px; width: auto; transition: 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
        .navbar-brand-custom { display: flex; align-items: center; gap: 12px; text-decoration: none !important; }
        .brand-main { display: block; color: white; font-weight: 800; font-size: 1.25rem; letter-spacing: 0.5px; transition: 0.3s; }
        .brand-sub { display: block; color: #00ced1; font-weight: 600; font-size: 0.7rem; letter-spacing: 2.5px; text-transform: uppercase; opacity: 0.9; }

        /* Search Pill */
        .search-pill-trigger {
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.1);
          color: rgba(255,255,255,0.8);
          padding: 9px 18px;
          border-radius: 50px;
          cursor: pointer;
          font-size: 0.85rem;
          transition: 0.3s;
          display: flex;
          align-items: center;
        }
        .search-pill-trigger:hover { 
          background: rgba(0, 206, 209, 0.15); 
          border-color: #00ced1; 
          color: white; 
          box-shadow: 0 0 20px rgba(0, 206, 209, 0.2);
        }

        /* Custom Mobile Toggle */
        .mobile-toggle-btn {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          width: 40px;
          height: 40px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.3s;
        }
        .mobile-toggle-btn:hover { background: rgba(0, 206, 209, 0.1); border-color: #00ced1; }
        
        .hamburger-box { width: 20px; height: 14px; position: relative; }
        .hamburger-inner {
          width: 100%;
          height: 2px;
          background-color: #00ced1;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          transition: 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
        }
        .hamburger-inner::before, .hamburger-inner::after {
          content: "";
          width: 100%;
          height: 2px;
          background-color: #00ced1;
          position: absolute;
          transition: 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
        }
        .hamburger-inner::before { top: -6px; }
        .hamburger-inner::after { bottom: -6px; }
        .active .hamburger-inner { transform: rotate(45deg); }
        .active .hamburger-inner::before { top: 0; opacity: 0; }
        .active .hamburger-inner::after { bottom: 0; transform: rotate(-90deg); }

        /* Navigation Links */
        .main-navigation-bar { 
          padding: 0 !important; 
          background: transparent;
        }
        .nav-link { 
          font-size: 0.8rem !important; 
          text-transform: uppercase; 
          font-weight: 700 !important; 
          letter-spacing: 0.8px; 
          color: rgba(255,255,255,0.85) !important;
          padding: 14px 20px !important;
          position: relative;
          transition: 0.3s;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: #00ced1;
          transition: 0.3s;
          transform: translateX(-50%);
        }
        .nav-link:hover::after, .nav-active::after { width: 30%; }
        .nav-link:hover, .nav-active { color: #00ced1 !important; }
        .ico-nav { font-size: 1.1rem; opacity: 0.9; }
        
        .tour-btn { 
          background: #00ced1 !important; 
          color: #002b5a !important;
          border-radius: 6px; 
          padding: 10px 28px !important;
          font-weight: 800 !important;
          box-shadow: 0 4px 15px rgba(0, 206, 209, 0.2);
        }
        .tour-btn:hover { 
          background: #fff !important; 
          transform: translateY(-2px); 
          box-shadow: 0 6px 20px rgba(0, 206, 209, 0.3);
        }

        /* Desktop Dropdown Premium Overhaul */
        @media (min-width: 992px) {
          .nav-item.dropdown:hover > .dropdown-menu { 
            display: block; 
            opacity: 1; 
            transform: translateY(0); 
            visibility: visible; 
          }
          
          .dropdown-menu {
            display: block;
            opacity: 0;
            visibility: hidden;
            transform: translateY(15px);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            background: rgba(0, 43, 90, 0.95) !important;
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border: 1px solid rgba(0, 206, 209, 0.2) !important;
            border-radius: 16px !important;
            box-shadow: 0 20px 50px rgba(0,0,0,0.3) !important;
            padding: 12px !important;
            min-width: 240px;
            margin-top: 0 !important;
          }

          .dropdown-item {
            color: rgba(255,255,255,0.85) !important;
            padding: 11px 18px !important;
            border-radius: 10px !important;
            font-size: 0.85rem !important;
            font-weight: 600 !important;
            transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 2px;
          }

          .dropdown-item:hover {
            background: rgba(0, 206, 209, 0.15) !important;
            color: #00ced1 !important;
            transform: translateX(5px);
          }

          .drop-ico { 
            font-size: 1.1rem; 
            color: #00ced1; 
            transition: 0.3s;
          }

          .dropdown-divider {
            border-top: 1px solid rgba(255,255,255,0.08) !important;
            margin: 8px 10px !important;
          }
        }

        /* Scroll Animation Triggers */
        .header-scrolled .branding-row { 
          max-height: 0; 
          padding: 0; 
          opacity: 0; 
          pointer-events: none; 
          transform: translateY(-20px);
          border-bottom: none;
        }
        .header-scrolled .top-ticker-bar { 
          max-height: 0; 
          padding: 0; 
          opacity: 0; 
          pointer-events: none; 
          transform: translateY(-10px);
        }
        .header-scrolled .nav-link { 
          padding: 10px 20px !important; 
          font-size: 0.75rem !important;
        }
        .header-scrolled .site-logo {
          height: 40px;
        }

        /* Mobile View - Smooth Drawer */
        @media (max-width: 991px) {
          .main-header-wrapper { position: sticky; width: 100%; top: 0; margin-bottom: 20px; }
          .branding-row { max-height: 70px; padding: 10px 0; background: #002b5a; }
          .site-logo { height: 45px; }
          .brand-main { font-size: 1.05rem; }
          .brand-sub { font-size: 0.65rem; letter-spacing: 1.5px; }
          
          .navbar-collapse {
            background: #001a38;
            max-height: 85vh;
            overflow-y: auto;
            border-bottom: 3px solid #00ced1;
            padding: 30px 0;
            box-shadow: 0 20px 40px rgba(0,0,0,0.4);
          }
          .nav-link {
            padding: 16px 30px !important;
            border-bottom: 1px solid rgba(255,255,255,0.03);
            font-size: 0.95rem !important;
            text-align: left;
          }
          .nav-link::after { bottom: 0; left: 0; transform: none; display: none; }
          
          .dropdown-menu {
            background: rgba(255,255,255,0.02) !important;
            border-left: 2px solid #00ced1;
            margin: 0 30px !important;
            border-radius: 0;
            padding: 5px 0 15px 15px;
          }
          .dropdown-item { 
            color: rgba(255,255,255,0.7) !important; 
            padding: 12px 15px; 
            font-size: 0.9rem;
          }
          .dropdown-item:hover { background: transparent !important; color: #00ced1 !important; }
          
          .header-scrolled { background: #002b5a !important; }
        }

        @media (max-width: 576px) {
          .search-pill-trigger { padding: 8px 12px; }
          .brand-main { font-size: 0.95rem; }
          .ticker-text { font-size: 0.75rem; }
        }
      `}</style>
    </header>
  );
};

export default memo(Header);